
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useNotification } from '../../components/member/NotificationProvider';
import { supabase } from '../../lib/supabase';
import { 
    getTournamentEntries, 
    updateEntryStatus, 
    finalizeTournament, 
    cancelTournament,
    startTournament,
    updateTournamentStatus,
    getMyStore,
    createTournamentTicketProduct,
    addTournamentSlot,
    assignTournamentParticipant,
    searchUsers,
    checkUserPaidTournamentTicket
} from '../../services/supabaseService';

export const ManageTournamentDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { showNotification } = useNotification();
    const [tournament, setTournament] = useState<any>(null);
    const [entries, setEntries] = useState<any[]>([]);
    const [entriesTab, setEntriesTab] = useState<'approved' | 'pending' | 'rejected'>('approved');
    const [store, setStore] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [previewDeck, setPreviewDeck] = useState<any>(null);
    const [targetStatusToConfirm, setTargetStatusToConfirm] = useState<string | null>(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [paidTicketWarningEntry, setPaidTicketWarningEntry] = useState<any | null>(null);
    const [checkingPaidTicket, setCheckingPaidTicket] = useState(false);

    // Export participants state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState<'simple' | 'detailed'>('simple');
    const [copiedList, setCopiedList] = useState(false);

    // Ticket creation state
    const [creatingTicket, setCreatingTicket] = useState(false);
    const [ticketPrice, setTicketPrice] = useState<string>('10.00');
    const [ticketQuantity, setTicketQuantity] = useState<string>('8');
    const [ticketMaxPerUser, setTicketMaxPerUser] = useState<string>('1');
    const [ticketIsActive, setTicketIsActive] = useState<boolean>(true);
    const [showTicketForm, setShowTicketForm] = useState(false);
    const [addingSlot, setAddingSlot] = useState(false);

    // Assign participant modal state
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedEntryToAssign, setSelectedEntryToAssign] = useState<any>(null);
    const [assignSearchQuery, setAssignSearchQuery] = useState('');
    const [assignSearchResults, setAssignSearchResults] = useState<any[]>([]);
    const [assignSearching, setAssignSearching] = useState(false);
    const [selectedTargetUser, setSelectedTargetUser] = useState<any>(null);
    const [assignSubmitting, setAssignSubmitting] = useState(false);

    const getStatusLabel = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'open': return 'Aberto';
            case 'in_progress':
            case 'running': return 'Em Andamento';
            case 'finished': return 'Finalizado';
            case 'scheduled': return 'Agendado';
            case 'cancelled':
            case 'canceled': return 'Cancelado';
            default: return status || 'Indefinido';
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const [myStore, { data: tourneyData, error }] = await Promise.all([
              getMyStore(),
              supabase
                .from('tournaments')
                .select(`
                  *, 
                  games(name), 
                  game_formats:game_formats!tournaments_format_id_fkey(name)
                `)
                .eq('id', id)
                .maybeSingle()
            ]);

            if (myStore) setStore(myStore);

            if (error) throw error;
            if (tourneyData) {
              let tournamentTickets: any[] = [];
              try {
                let ticketProds: any[] | null = null;
                const { data: byTournamentId } = await supabase
                  .from('products')
                  .select('*, store_products(*)')
                  .eq('product_type_id', 52)
                  .eq('tournament_id', tourneyData.id);

                if (byTournamentId && byTournamentId.length > 0) {
                  ticketProds = byTournamentId;
                } else {
                  const { data: byName } = await supabase
                    .from('products')
                    .select('*, store_products(*)')
                    .eq('product_type_id', 52)
                    .ilike('name', `%${tourneyData.name}%`);
                  ticketProds = byName;
                }

                const entriesData = await getTournamentEntries(id);
                setEntries(entriesData);

                if (ticketProds && ticketProds.length > 0) {
                  const prod = ticketProds[0];
                  const sp = Array.isArray(prod.store_products) && prod.store_products.length > 0 ? prod.store_products[0] : null;
                  const maxQ = Number(tourneyData.max_players) || Number(sp?.quantity) || 8;
                  const soldQ = entriesData?.filter((e: any) => e.status !== 'rejected').length || 0;
                  tournamentTickets = [{
                    id: prod.id,
                    product_id: prod.id,
                    tournament_id: tourneyData.id,
                    max_quantity: maxQ,
                    sold_quantity: soldQ,
                    is_active: prod.is_available !== false,
                    max_per_user: sp?.max_per_user ?? 1,
                    product: {
                      ...prod,
                      msrp: sp?.store_price ?? prod.msrp ?? 0
                    }
                  }];
                }
              } catch (tErr) {
                console.warn('Could not load ticket product for tournament:', tErr);
              }

              setTournament({
                ...tourneyData,
                game_formats: tourneyData.game_formats || tourneyData.tournament_formats,
                tournament_formats: tourneyData.game_formats || tourneyData.tournament_formats,
                tickets: tournamentTickets
              });
              if (tourneyData.max_players) {
                setTicketQuantity(String(tourneyData.max_players));
              }
              const existingTicket = tournamentTickets[0] || null;
              if (existingTicket) {
                setTicketPrice(String(existingTicket.product?.msrp || 10));
                setTicketMaxPerUser(String(existingTicket.max_per_user || 1));
                setTicketIsActive(existingTicket.is_active !== false);
              }
            } else {
              setTournament(null);
            }
        } catch (error) {
            showNotification("Erro ao carregar dados", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAddSlot = async () => {
        if (!tournament?.id) return;
        if (tournament.status !== 'open' && tournament.status !== 'scheduled') {
            showNotification("Não é possível adicionar vagas após o início do torneio.", "error");
            return;
        }
        setAddingSlot(true);
        try {
            const res = await addTournamentSlot(tournament.id, store?.id || tournament.store_id);
            if (res && res.success) {
                const newMax = res.new_max_players;
                setTournament((prev: any) => prev ? {
                    ...prev,
                    max_players: newMax,
                    tickets: (prev.tickets || []).map((t: any) => ({
                        ...t,
                        max_quantity: newMax,
                        product: t.product ? { ...t.product } : t.product
                    }))
                } : prev);
                setTicketQuantity(String(newMax));
                showNotification(`Vaga adicionada com sucesso! O torneio e o estoque agora possuem ${newMax} vagas sincronizadas.`, "success");
                await fetchData();
            } else {
                showNotification(res?.error || "Erro ao adicionar vaga ao torneio.", "error");
            }
        } catch (error: any) {
            showNotification(error?.message || "Erro ao adicionar vaga ao torneio.", "error");
        } finally {
            setAddingSlot(false);
        }
    };

    const handleOpenAssignModal = async (entry: any) => {
        if (tournament?.status !== 'open' && tournament?.status !== 'scheduled') {
            showNotification("Não é possível alterar participantes após o início do torneio.", "error");
            return;
        }
        setSelectedEntryToAssign(entry);
        setSelectedTargetUser(null);
        setAssignSearchQuery('');
        setShowAssignModal(true);
        setAssignSearching(true);
        try {
            const initialUsers = await searchUsers('');
            setAssignSearchResults(initialUsers || []);
        } catch (err) {
            console.error('Error fetching initial users for assign:', err);
            setAssignSearchResults([]);
        } finally {
            setAssignSearching(false);
        }
    };

    const handleSearchUsers = async (query: string) => {
        setAssignSearchQuery(query);
        setAssignSearching(true);
        try {
            const results = await searchUsers(query);
            setAssignSearchResults(results || []);
        } catch (err) {
            console.error('Error searching users for assign:', err);
            setAssignSearchResults([]);
        } finally {
            setAssignSearching(false);
        }
    };

    const handleConfirmAssign = async () => {
        if (!selectedEntryToAssign || !selectedTargetUser || !tournament?.id) return;
        setAssignSubmitting(true);
        try {
            const res = await assignTournamentParticipant({
                tournamentId: tournament.id,
                entryId: selectedEntryToAssign.id,
                userId: selectedTargetUser.id,
                storeId: store?.id || tournament.store_id
            });

            if (res && res.error) {
                showNotification(res.error, "error");
                return;
            }

            showNotification(
                `Jogador @${selectedTargetUser.username} atribuído com sucesso à vaga!`,
                "success"
            );
            setShowAssignModal(false);
            setSelectedEntryToAssign(null);
            setSelectedTargetUser(null);
            await fetchData();
        } catch (err: any) {
            console.error("Error confirming participant assignment:", err);
            showNotification(err.message || "Erro ao atribuir usuário.", "error");
        } finally {
            setAssignSubmitting(false);
        }
    };

    const handleCreateTicketSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tournament) return;
        if (tournament.status !== 'open' && tournament.status !== 'scheduled') {
            showNotification("Não é possível alterar vagas ou ingressos após o início do torneio.", "error");
            return;
        }
        setCreatingTicket(true);
        try {
            const requestedQty = Number(ticketQuantity) || Number(tournament.max_players) || 8;
            const res = await createTournamentTicketProduct(
                tournament,
                {
                    ticket_price: parseFloat(ticketPrice) || 0,
                    ticket_quantity: requestedQty,
                    max_quantity: requestedQty,
                    max_per_user: Math.max(1, parseInt(ticketMaxPerUser) || 1),
                    is_active: ticketIsActive
                },
                store?.id || tournament.store_id
            );

            if (res && res.success) {
                // Mantém tournaments.max_players estritamente sincronizado com a quantity de store_products
                await supabase
                    .from('tournaments')
                    .update({ max_players: requestedQty })
                    .eq('id', tournament.id);

                showNotification("Ingresso configurado e estoque atualizado com sucesso!", "success");
                setShowTicketForm(false);
                await fetchData();
            } else {
                showNotification((res as any)?.error || "Erro ao processar ingresso", "error");
            }
        } catch (error: any) {
            showNotification(error?.message || "Erro ao processar ingresso", "error");
        } finally {
            setCreatingTicket(false);
        }
    };

    const applyStatusChange = async (entryId: number, status: string) => {
        try {
            const res = await updateEntryStatus(entryId, status);
            if (res && (res as any).error) {
                showNotification((res as any).error, "error");
                return;
            }
            const statusLabel = status === 'approved' 
                ? 'Aprovado' 
                : status === 'rejected' 
                ? 'Recusado (vaga liberada)' 
                : 'Pendente';
            showNotification(`Status do participante atualizado para ${statusLabel}`, "success");
            
            const updatedEntries = entries.map(e => e.id === entryId ? { ...e, status } : e);
            setEntries(updatedEntries);

            // Mantém sold_quantity dos ingressos sincronizado imediatamente (excluindo rejeitadas)
            const newSoldQ = updatedEntries.filter((e: any) => e.status !== 'rejected').length;
            setTournament((prev: any) => prev ? {
                ...prev,
                tickets: (prev.tickets || []).map((t: any) => ({
                    ...t,
                    sold_quantity: newSoldQ
                }))
            } : prev);
        } catch (error) {
            showNotification("Erro ao atualizar status", "error");
        }
    };

    const handleStatusChange = async (entryId: number, status: string) => {
        if (tournament?.status !== 'open' && tournament?.status !== 'scheduled') {
            showNotification("Não é possível alterar o status dos participantes após o início do torneio.", "error");
            return;
        }

        const targetEntry = entries.find(e => e.id === entryId);
        // Se estiver tentando reativar ou mudar o status de uma inscrição rejeitada para ativa (aprovada ou pendente)
        if (targetEntry && targetEntry.status === 'rejected' && status !== 'rejected') {
            const currentActive = entries.filter(e => e.status !== 'rejected').length;
            const maxPlayers = Number(tournament?.max_players) || 8;
            if (currentActive >= maxPlayers) {
                showNotification(`Não há vagas disponíveis para reativar esta inscrição (${currentActive}/${maxPlayers} vagas preenchidas).`, "error");
                return;
            }
        }

        // Se estiver rejeitando, verifica se o usuário comprou ingresso pago usando has_paid_tournament_ticket
        if (status === 'rejected' && targetEntry) {
            const userId = targetEntry.user_id || targetEntry.user?.id;
            if (userId && tournament?.id) {
                setCheckingPaidTicket(true);
                try {
                    const hasPaid = await checkUserPaidTournamentTicket(userId, tournament.id);
                    if (hasPaid) {
                        setPaidTicketWarningEntry(targetEntry);
                        setCheckingPaidTicket(false);
                        return;
                    }
                } catch (err) {
                    console.warn('Erro ao verificar ingresso pago do participante:', err);
                } finally {
                    setCheckingPaidTicket(false);
                }
            }
        }

        await applyStatusChange(entryId, status);
    };

    const confirmRejectPaidEntry = async () => {
        if (!paidTicketWarningEntry) return;
        const entryId = paidTicketWarningEntry.id;
        setPaidTicketWarningEntry(null);
        await applyStatusChange(entryId, 'rejected');
    };

    const handleApproveAll = async () => {
        if (tournament?.status !== 'open' && tournament?.status !== 'scheduled') {
            showNotification("Não é possível alterar o status dos participantes após o início do torneio.", "error");
            return;
        }
        const pending = entries.filter(e => e.status === 'pending');
        if (pending.length === 0) return;
        try {
            for (const entry of pending) {
                await updateEntryStatus(entry.id, 'approved');
            }
            showNotification(`${pending.length} participante(s) aprovado(s) com sucesso!`, "success");
            setEntries(prev => prev.map(e => e.status === 'pending' ? { ...e, status: 'approved' } : e));
        } catch (err: any) {
            showNotification(err?.message || "Erro ao aprovar participantes", "error");
            fetchData();
        }
    };

    const handleStart = async () => {
        if (!id) return;
        const active = entries.filter(e => e.status !== 'rejected');
        if (active.length === 0) {
            showNotification("O torneio precisa de pelo menos 1 participante aprovado para ser iniciado.", "error");
            return;
        }
        const pending = entries.filter(e => e.status === 'pending');
        if (pending.length > 0) {
            showNotification(`Todos os participantes ativos devem estar aprovados para iniciar o torneio (${pending.length} participante(s) pendente(s)).`, "error");
            return;
        }
        try {
            const res = await startTournament(id);
            if (res && 'success' in res && res.success) {
                showNotification("Torneio iniciado!", "success");
                fetchData();
            } else if (res && (res as any).error) {
                showNotification((res as any).error, "error");
            }
        } catch (error: any) {
            showNotification(error?.message || "Erro ao iniciar", "error");
        }
    };

    const handleFinalize = async () => {
        if (!id) return;
        if (isFinished || isCancelled) {
            showNotification("Este torneio já foi finalizado ou cancelado e seu status não pode ser alterado.", "error");
            return;
        }
        setTargetStatusToConfirm('finished');
    };

    const handleSelectStatus = (newStatus: string) => {
        if (!id || !tournament) return;
        if (isFinished || isCancelled) {
            showNotification("Este torneio já foi finalizado ou cancelado e seu status não pode mais ser alterado.", "error");
            return;
        }

        const currentNorm = (tournament.status || '').toLowerCase();
        const targetNorm = newStatus.toLowerCase();
        if (currentNorm === targetNorm || (targetNorm === 'in_progress' && (currentNorm === 'in_progress' || currentNorm === 'running'))) {
            return;
        }

        // Se for finalização ou cancelamento, requer confirmação de ação definitiva
        if (targetNorm === 'finished' || targetNorm === 'cancelled' || targetNorm === 'canceled') {
            setTargetStatusToConfirm(targetNorm === 'canceled' ? 'cancelled' : targetNorm);
            return;
        }

        executeStatusChange(targetNorm);
    };

    const executeStatusChange = async (newStatus: string) => {
        if (!id) return;
        setUpdatingStatus(true);
        try {
            const res = await updateTournamentStatus(id, newStatus);
            if (res && 'success' in res && res.success) {
                const label = getStatusLabel(newStatus);
                const isDefinitive = newStatus === 'finished' || newStatus === 'cancelled' || newStatus === 'canceled';
                showNotification(
                    isDefinitive 
                        ? `Torneio ${label.toLowerCase()} com sucesso! O status foi consolidado em definitivo.`
                        : `Status do torneio alterado para "${label}" com sucesso!`,
                    "success"
                );
                setTargetStatusToConfirm(null);
                await fetchData();
            } else {
                showNotification((res as any)?.error || "Erro ao alterar status do torneio", "error");
            }
        } catch (err: any) {
            showNotification(err?.message || "Erro ao alterar status do torneio", "error");
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Helper functions to export participants
    const activeEntries = entries.filter(e => e.status !== 'rejected');
    const approvedEntries = entries.filter(e => e.status === 'approved');
    const pendingEntries = entries.filter(e => e.status === 'pending');
    const rejectedEntries = entries.filter(e => e.status === 'rejected');

    const getSimpleNamesList = () => {
        const listToExport = entriesTab === 'rejected' ? rejectedEntries : activeEntries;
        return listToExport
            .map((entry, idx) => {
                const name = entry.user?.fullname || entry.user?.username || `Participante ${idx + 1}`;
                return `${idx + 1}. ${name}`;
            })
            .join('\n');
    };

    const getDetailedNamesList = () => {
        const listToExport = entriesTab === 'rejected' ? rejectedEntries : activeEntries;
        const tabLabel = entriesTab === 'rejected' ? 'Participações Rejeitadas' : 'Inscritos Ativos';
        const header = `${tournament?.name || 'Torneio'} - Lista de ${tabLabel}\nData: ${tournament?.starts_at ? new Date(tournament.starts_at).toLocaleDateString('pt-BR') : 'A definir'}\nTotal: ${listToExport.length}\n----------------------------------------\n`;
        const lines = listToExport.map((entry, idx) => {
            const name = entry.user?.fullname || entry.user?.username || `Participante ${idx + 1}`;
            const username = entry.user?.username ? `@${entry.user.username}` : '';
            const deck = entry.tournament_deck?.name ? `Deck: ${entry.tournament_deck.name}` : '';
            const pos = entry.final_position ? `${entry.final_position}º lugar` : '';
            const status = entry.status === 'approved' ? 'Aprovado' : entry.status === 'rejected' ? 'Rejeitado' : 'Pendente';
            const extras = [username, deck, pos, status].filter(Boolean).join(' | ');
            return `${idx + 1}. ${name}${extras ? ` (${extras})` : ''}`;
        });
        return header + lines.join('\n');
    };

    const handleCopyList = async () => {
        const text = exportFormat === 'simple' ? getSimpleNamesList() : getDetailedNamesList();
        try {
            await navigator.clipboard.writeText(text);
            setCopiedList(true);
            showNotification('Lista copiada para a área de transferência!', 'success');
            setTimeout(() => setCopiedList(false), 2500);
        } catch {
            showNotification('Não foi possível copiar automaticamente.', 'error');
        }
    };

    const handleDownloadCsv = () => {
        const listToExport = entriesTab === 'rejected' ? rejectedEntries : activeEntries;
        if (listToExport.length === 0) return;
        const sanitize = (val: any) => `"${String(val ?? '').replace(/"/g, '""')}"`;
        const rows = [
            ['#', 'Nome', 'Usuario', 'Status', 'Posicao', 'Deck'].map(sanitize).join(';')
        ];

        listToExport.forEach((entry, idx) => {
            rows.push([
                idx + 1,
                entry.user?.fullname || entry.user?.username || `Participante ${idx + 1}`,
                entry.user?.username || '',
                entry.status === 'approved' ? 'Aprovado' : entry.status === 'rejected' ? 'Rejeitado' : 'Pendente',
                entry.final_position ? `${entry.final_position}º` : '',
                entry.tournament_deck?.name || ''
            ].map(sanitize).join(';'));
        });

        const csvContent = '\uFEFF' + rows.join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeTitle = (tournament?.name || 'torneio').toLowerCase().replace(/[^a-z0-9]/g, '_');
        link.href = url;
        link.setAttribute('download', `participantes_${safeTitle}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showNotification('Arquivo CSV exportado com sucesso!', 'success');
    };

    const handleDownloadTxt = () => {
        const listToExport = entriesTab === 'rejected' ? rejectedEntries : activeEntries;
        if (listToExport.length === 0) return;
        const text = exportFormat === 'simple' ? getSimpleNamesList() : getDetailedNamesList();
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeTitle = (tournament?.name || 'torneio').toLowerCase().replace(/[^a-z0-9]/g, '_');
        link.href = url;
        link.setAttribute('download', `participantes_${safeTitle}.txt`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showNotification('Arquivo TXT exportado com sucesso!', 'success');
    };

    if (loading) return <div className="p-8 text-center">Carregando...</div>;
    if (!tournament) return <div className="p-8 text-center">Torneio não encontrado</div>;

    const isFinished = tournament.status === 'finished';
    const isCancelled = tournament.status === 'cancelled' || tournament.status === 'canceled';
    const isOpen = tournament.status === 'open';
    const isScheduled = tournament.status === 'scheduled';
    const canManage = isOpen || isScheduled;

    // Participações ativas (rejeitadas são excluídas das vagas ocupadas)
    const allApproved = activeEntries.length > 0 && pendingEntries.length === 0;
    const unapprovedCount = pendingEntries.length;

    const rankedEntries = [...entries]
        .filter(e => e.status !== 'rejected' && e.final_position !== null && e.final_position !== undefined && Number(e.final_position) >= 1)
        .sort((a, b) => Number(a.final_position) - Number(b.final_position));

    return (
        <div className="space-y-8 max-w-5xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/meus-torneios" className="text-purple-400 text-xs font-bold hover:underline mb-2 block italic">
                        <i className="fas fa-arrow-left mr-1"></i> VOLTAR AOS MEUS TORNEIOS
                    </Link>
                    <h1 className="text-3xl font-black text-white italic tracking-tighter uppercase">{tournament.name}</h1>
                    <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{tournament.games?.name}</span>
                        <span className="text-slate-700">•</span>
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{tournament.game_formats?.name || tournament.tournament_formats?.name}</span>
                        {tournament.current_round > 0 && (
                            <>
                                <span className="text-slate-700">•</span>
                                <span className="text-xs font-black text-purple-400 uppercase tracking-widest italic">Rodada {tournament.current_round}</span>
                            </>
                        )}
                    </div>
                </div>

                <div className="text-right flex flex-col items-end">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Status Atual</span>
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-black px-4 py-2 rounded-xl border italic ${
                            isCancelled 
                                ? 'text-rose-400 bg-rose-500/10 border-rose-500/30' 
                                : isFinished 
                                    ? 'text-slate-400 bg-slate-800 border-white/5' 
                                    : isScheduled
                                        ? 'text-amber-400 bg-amber-500/10 border-amber-500/30'
                                        : isOpen 
                                            ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' 
                                            : 'text-blue-400 bg-blue-500/10 border-blue-500/30'
                        }`}>
                            {isCancelled ? 'CANCELADO' : isFinished ? 'FINALIZADO' : isScheduled ? 'AGENDADO' : isOpen ? 'ABERTO' : 'EM ANDAMENTO'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    {/* Participants List */}
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                                <h3 className="font-black text-white italic tracking-tighter uppercase">
                                    Inscritos ({activeEntries.length}/{tournament.max_players})
                                </h3>
                                <span className="text-[10px] font-bold text-slate-400 bg-slate-800 border border-slate-700/60 px-2 py-0.5 rounded-md">
                                    {Math.max(0, tournament.max_players - activeEntries.length)} vagas livres
                                </span>
                                {canManage && (
                                    <button
                                        type="button"
                                        onClick={handleAddSlot}
                                        disabled={addingSlot || !canManage}
                                        className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 hover:text-white border border-purple-500/40 text-[10px] font-black px-2.5 py-1.5 rounded-xl transition-all uppercase tracking-wider flex items-center space-x-1.5 shadow-sm active:scale-95 disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
                                        title="Aumentar em 1 vaga o limite do torneio e o estoque do produto de ingresso"
                                    >
                                        <i className={`fas ${addingSlot ? 'fa-spinner fa-spin' : 'fa-plus'} text-[9px] text-purple-400`}></i>
                                        <span>Adicionar Vaga</span>
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    id="btn-export-participants"
                                    onClick={() => setShowExportModal(true)}
                                    disabled={entries.length === 0}
                                    className="bg-purple-950/60 hover:bg-purple-900/80 disabled:opacity-40 disabled:pointer-events-none text-purple-300 hover:text-white border border-purple-500/30 text-[10px] font-black px-3 py-2 rounded-xl transition-all uppercase tracking-wider flex items-center space-x-1.5 shadow-sm cursor-pointer"
                                    title="Exportar lista de participantes (CSV, TXT ou Área de Transferência)"
                                >
                                    <i className="fas fa-file-arrow-down text-purple-400"></i>
                                    <span>Exportar Lista</span>
                                </button>
                                {canManage && (
                                    <button 
                                        onClick={handleStart}
                                        disabled={!allApproved}
                                        className={`text-[10px] font-black px-4 py-2 rounded-xl transition-all uppercase tracking-widest flex items-center space-x-1.5 ${
                                            allApproved 
                                                ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20 active:scale-95 cursor-pointer' 
                                                : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700/50'
                                        }`}
                                        title={
                                            activeEntries.length === 0
                                                ? 'Adicione ou aprove participantes para iniciar o torneio'
                                                : !allApproved
                                                ? `Aprove todos os participantes ativos para iniciar (${unapprovedCount} pendente(s))`
                                                : 'Iniciar torneio'
                                        }
                                    >
                                        <i className="fas fa-play text-[9px]"></i>
                                        <span>INICIAR TORNEIO</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Tabs de Separação de Participações */}
                        <div className="flex border-b border-slate-800 bg-slate-950/60 px-6 pt-2.5 gap-2 overflow-x-auto">
                            <button
                                type="button"
                                onClick={() => setEntriesTab('approved')}
                                className={`px-4 py-2 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 cursor-pointer shrink-0 ${
                                    entriesTab === 'approved'
                                        ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10 rounded-t-xl'
                                        : 'border-transparent text-slate-400 hover:text-slate-200'
                                }`}
                            >
                                <i className="fas fa-circle-check text-[11px]"></i>
                                <span>Aprovadas</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                    entriesTab === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-800 text-slate-400'
                                }`}>
                                    {approvedEntries.length}
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setEntriesTab('pending')}
                                className={`px-4 py-2 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 cursor-pointer shrink-0 ${
                                    entriesTab === 'pending'
                                        ? 'border-amber-500 text-amber-400 bg-amber-500/10 rounded-t-xl'
                                        : 'border-transparent text-slate-400 hover:text-slate-200'
                                }`}
                            >
                                <i className="fas fa-clock text-[11px]"></i>
                                <span>Pendentes</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                    pendingEntries.length > 0 
                                        ? 'bg-amber-500/20 text-amber-300 font-black animate-pulse' 
                                        : 'bg-slate-800 text-slate-400'
                                }`}>
                                    {pendingEntries.length}
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setEntriesTab('rejected')}
                                className={`px-4 py-2 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 cursor-pointer shrink-0 ${
                                    entriesTab === 'rejected'
                                        ? 'border-rose-500 text-rose-400 bg-rose-500/10 rounded-t-xl'
                                        : 'border-transparent text-slate-400 hover:text-rose-300'
                                }`}
                            >
                                <i className="fas fa-ban text-[11px]"></i>
                                <span>Rejeitadas</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                    entriesTab === 'rejected' ? 'bg-rose-500/20 text-rose-300' : 'bg-slate-800 text-slate-400'
                                }`}>
                                    {rejectedEntries.length}
                                </span>
                                {rejectedEntries.length > 0 && (
                                    <span className="text-[9px] bg-rose-500/10 text-rose-400 border border-rose-500/20 px-1.5 py-0.2 rounded font-mono hidden sm:inline">
                                        Vagas liberadas
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* Banner quando na aba Rejeitadas explicando que liberam a vaga */}
                        {entriesTab === 'rejected' && (
                            <div className="bg-rose-500/10 border-b border-rose-500/20 px-6 py-3 flex items-center space-x-2.5 text-xs text-rose-300">
                                <i className="fas fa-info-circle text-rose-400 text-sm shrink-0"></i>
                                <span>
                                    As inscrições rejeitadas <strong>não contam</strong> para o total de inscritos do torneio e <strong>liberam automaticamente a vaga</strong> para outros participantes.
                                </span>
                            </div>
                        )}

                        {/* Banner quando existem participantes pendentes */}
                        {canManage && pendingEntries.length > 0 && entriesTab !== 'rejected' && (
                            <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center space-x-2 text-amber-300 text-xs">
                                    <i className="fas fa-triangle-exclamation text-amber-400 text-sm"></i>
                                    <span>
                                        Existem <strong>{pendingEntries.length}</strong> participante(s) aguardando aprovação. Todos os inscritos ativos devem estar com status <strong>Aprovado</strong> para iniciar o evento.
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {entriesTab !== 'pending' && (
                                        <button
                                            type="button"
                                            onClick={() => setEntriesTab('pending')}
                                            className="bg-slate-800 hover:bg-slate-700 text-amber-300 text-[10px] font-black px-2.5 py-1.5 rounded-lg uppercase tracking-wider transition-all border border-amber-500/30 cursor-pointer"
                                        >
                                            Ver Pendentes ({pendingEntries.length})
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleApproveAll}
                                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-[10px] px-3 py-1.5 rounded-lg uppercase tracking-wider transition-all shadow-sm active:scale-95 flex items-center space-x-1 cursor-pointer"
                                        title="Aprovar todas as inscrições pendentes"
                                    >
                                        <i className="fas fa-check-double text-[9px]"></i>
                                        <span>Aprovar Todos ({pendingEntries.length})</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Banner indicando que o torneio já iniciou e os participantes/vagas estão congelados */}
                        {!canManage && !isCancelled && (
                            <div className="bg-blue-500/10 border-b border-blue-500/20 px-6 py-2.5 flex items-center space-x-2 text-blue-300 text-xs">
                                <i className="fas fa-lock text-blue-400"></i>
                                <span>
                                    {isFinished 
                                        ? 'Torneio finalizado. As vagas e participantes estão gravados e protegidos contra alterações.'
                                        : 'Torneio em andamento. As inscrições e o número de vagas estão congelados.'}
                                </span>
                            </div>
                        )}

                        <div className="divide-y divide-slate-800">
                            {(() => {
                                const currentTabEntries = entriesTab === 'approved' 
                                    ? approvedEntries 
                                    : entriesTab === 'pending' 
                                    ? pendingEntries 
                                    : rejectedEntries;

                                if (currentTabEntries.length === 0) {
                                    if (entriesTab === 'approved') {
                                        return (
                                            <div className="p-12 text-center text-slate-500 text-sm space-y-2">
                                                <i className="fas fa-user-check text-2xl text-slate-600 block mb-1"></i>
                                                <p>Nenhuma participação aprovada ainda.</p>
                                                {pendingEntries.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setEntriesTab('pending')}
                                                        className="text-xs text-amber-400 hover:underline font-bold cursor-pointer"
                                                    >
                                                        Ver {pendingEntries.length} inscrição(ões) pendente(s)
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (entriesTab === 'pending') {
                                        return (
                                            <div className="p-12 text-center text-slate-500 text-sm">
                                                <i className="fas fa-check-double text-2xl text-emerald-500/50 block mb-1"></i>
                                                <p>Não há nenhuma inscrição pendente de aprovação.</p>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="p-12 text-center text-slate-500 text-sm">
                                            <i className="fas fa-ban text-2xl text-slate-600 block mb-1"></i>
                                            <p>Nenhuma participação foi rejeitada neste torneio.</p>
                                        </div>
                                    );
                                }

                                return currentTabEntries.map(entry => {
                                    const isUnassigned = !entry.user_id || entry.user_id === null;

                                    return (
                                        <div key={entry.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-white/[0.02] transition-colors">
                                            <div className="flex items-center space-x-4">
                                                {isUnassigned ? (
                                                    canManage ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenAssignModal(entry)}
                                                            disabled={isCancelled}
                                                            className="w-10 h-10 rounded-full bg-amber-500/10 hover:bg-amber-500/25 border-2 border-dashed border-amber-500/50 hover:border-amber-400 flex items-center justify-center text-amber-400 hover:text-amber-300 shrink-0 shadow-inner cursor-pointer transition-all active:scale-95 group relative"
                                                            title="Clique para atribuir um usuário a este participante"
                                                        >
                                                            <i className="fas fa-user-plus text-sm group-hover:scale-110 transition-transform"></i>
                                                        </button>
                                                    ) : (
                                                        <div
                                                            className="w-10 h-10 rounded-full bg-amber-500/10 border-2 border-dashed border-amber-500/30 flex items-center justify-center text-amber-400/60 shrink-0 select-none"
                                                            title="Participante sem usuário vinculado (congelado)"
                                                        >
                                                            <i className="fas fa-user-clock text-sm"></i>
                                                        </div>
                                                    )
                                                ) : (
                                                    canManage ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenAssignModal(entry)}
                                                            disabled={isCancelled}
                                                            className="w-10 h-10 rounded-full bg-slate-800 hover:ring-2 hover:ring-amber-500/50 border border-white/10 overflow-hidden shrink-0 shadow-inner cursor-pointer transition-all active:scale-95 relative group"
                                                            title="Clique para alterar usuário vinculado"
                                                        >
                                                            {entry.user?.avatar ? (
                                                                <img src={entry.user.avatar} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-slate-600 font-bold group-hover:text-slate-400">
                                                                    {entry.user?.username?.charAt(0) || entry.user?.fullname?.charAt(0) || '?'}
                                                                </div>
                                                            )}
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-amber-300 text-xs">
                                                                <i className="fas fa-user-pen"></i>
                                                            </div>
                                                        </button>
                                                    ) : (
                                                        <div
                                                            className="w-10 h-10 rounded-full bg-slate-800 border border-white/10 overflow-hidden shrink-0 shadow-inner select-none"
                                                        >
                                                            {entry.user?.avatar ? (
                                                                <img src={entry.user.avatar} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-slate-500 font-bold">
                                                                    {entry.user?.username?.charAt(0) || entry.user?.fullname?.charAt(0) || '?'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                )}
                                                <div>
                                                    <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                                        {isUnassigned ? (
                                                            <>
                                                                <span className="font-bold text-amber-300 text-sm">
                                                                    {entry.ticket_unit ? `Guest` : `Vaga sem jogador`}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="font-bold text-white text-sm">{entry.user?.fullname || entry.user?.username || 'Membro'}</span>
                                                                {entry.final_position !== null && entry.final_position !== undefined && Number(entry.final_position) >= 1 && (
                                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black font-mono border ${
                                                                        Number(entry.final_position) === 1
                                                                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                                                            : Number(entry.final_position) === 2
                                                                            ? 'bg-slate-300/20 text-slate-200 border-slate-300/40'
                                                                            : Number(entry.final_position) === 3
                                                                            ? 'bg-amber-700/20 text-amber-500 border-amber-700/40'
                                                                            : 'bg-purple-950/40 text-purple-300 border-purple-500/30'
                                                                    }`}>
                                                                        {Number(entry.final_position) === 1 ? '🥇 1º' : Number(entry.final_position) === 2 ? '🥈 2º' : Number(entry.final_position) === 3 ? '🥉 3º' : `${entry.final_position}º`}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    {isUnassigned ? (
                                                        <div className="text-[10px] text-slate-400 font-mono tracking-tight flex items-center space-x-1.5">
                                                            <span className="text-amber-400 font-semibold">Pendente de atribuição</span>
                                                            {entry.order_item_id && <span className="text-slate-500">• Item #{entry.order_item_id}</span>}
                                                        </div>
                                                    ) : (
                                                        <div className="text-[10px] text-slate-500 font-mono tracking-tight uppercase flex items-center space-x-1.5">
                                                            <span>@{entry.user?.username}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center flex-wrap gap-2.5 sm:justify-end">
                                                {/* Decklist */}
                                                {entry.tournament_deck ? (
                                                    <button
                                                        onClick={() => setPreviewDeck(entry.tournament_deck)}
                                                        className="bg-purple-950/40 hover:bg-purple-900/60 text-purple-300 border border-purple-500/30 text-[10px] font-bold px-2.5 py-1 rounded-lg transition-all flex items-center space-x-1 cursor-pointer"
                                                        title="Visualizar decklist do participante"
                                                    >
                                                        <i className="fas fa-layer-group text-purple-400"></i>
                                                        <span className="max-w-[100px] truncate">{entry.tournament_deck.name}</span>
                                                    </button>
                                                ) : (
                                                    <span className="text-[10px] text-slate-500 italic">
                                                        Sem decklist
                                                    </span>
                                                )}

                                                {/* Pontos (somente leitura) */}
                                                {entry.points !== null && entry.points !== undefined && Number(entry.points) > 0 && (
                                                    <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-950 border border-slate-800 px-2 py-1 rounded-lg select-none" title="Pontuação">
                                                        {entry.points} pts
                                                    </span>
                                                )}

                                                {/* Status & Ações por Aba (sem exclusão) */}
                                                {!canManage ? (
                                                    <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg border select-none ${
                                                        entry.status === 'approved'
                                                            ? 'bg-emerald-950/40 text-emerald-400 border-emerald-500/30'
                                                            : entry.status === 'rejected'
                                                            ? 'bg-rose-950/40 text-rose-400 border-rose-500/30'
                                                            : 'bg-amber-950/40 text-amber-400 border-amber-500/30'
                                                    }`}>
                                                        {entry.status === 'approved' ? 'Aprovado' : entry.status === 'rejected' ? 'Recusado' : 'Pendente'}
                                                    </span>
                                                ) : entry.status === 'pending' ? (
                                                    <div className="flex items-center space-x-1.5">
                                                        <button 
                                                            onClick={() => handleStatusChange(entry.id, 'approved')}
                                                            className="bg-emerald-600/10 hover:bg-emerald-600 text-emerald-400 hover:text-white text-[9px] font-black px-3 py-1.5 rounded-lg border border-emerald-600/30 transition-all uppercase tracking-wider cursor-pointer active:scale-95 flex items-center space-x-1"
                                                            title="Aprovar inscrição"
                                                        >
                                                            <i className="fas fa-check text-[9px]"></i>
                                                            <span>Aprovar</span>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleStatusChange(entry.id, 'rejected')}
                                                            className="bg-rose-600/10 hover:bg-rose-600 text-rose-400 hover:text-white text-[9px] font-black px-3 py-1.5 rounded-lg border border-rose-600/30 transition-all uppercase tracking-wider cursor-pointer active:scale-95 flex items-center space-x-1"
                                                            title="Recusar inscrição (libera a vaga para outro participante)"
                                                        >
                                                            <i className="fas fa-ban text-[9px]"></i>
                                                            <span>Recusar</span>
                                                        </button>
                                                    </div>
                                                ) : entry.status === 'rejected' ? (
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg border bg-rose-950/40 text-rose-400 border-rose-500/30">
                                                            Recusado
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleStatusChange(entry.id, 'approved')}
                                                            className="bg-emerald-600/10 hover:bg-emerald-600 text-emerald-400 hover:text-white text-[9px] font-black px-2.5 py-1 rounded-lg border border-emerald-600/30 transition-all uppercase tracking-wider cursor-pointer active:scale-95 flex items-center space-x-1"
                                                            title="Reconsiderar e aprovar inscrição (preencherá uma vaga livre)"
                                                        >
                                                            <i className="fas fa-rotate-left text-[9px]"></i>
                                                            <span>Reativar</span>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center space-x-2">
                                                        <select
                                                            value={entry.status}
                                                            onChange={(e) => handleStatusChange(entry.id, e.target.value)}
                                                            className="text-[10px] font-bold rounded-lg px-2.5 py-1 outline-none border transition-all cursor-pointer bg-emerald-950/40 text-emerald-400 border-emerald-500/30 hover:border-emerald-500/60"
                                                            title="Alterar status do participante"
                                                        >
                                                            <option value="approved" className="bg-slate-900 text-emerald-400">Aprovado</option>
                                                            <option value="pending" className="bg-slate-900 text-amber-400">Pendente</option>
                                                            <option value="rejected" className="bg-slate-900 text-rose-400">Rejeitar (Liberar Vaga)</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Banner: Torneio Cancelado */}
                    {isCancelled && (
                        <div className="bg-slate-900 border border-rose-500/30 rounded-2xl p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 shrink-0">
                                <i className="fas fa-ban text-lg"></i>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-rose-300 uppercase">Torneio Cancelado</h4>
                                <p className="text-xs text-slate-400">Este evento foi cancelado e está encerrado.</p>
                            </div>
                        </div>
                    )}

                    {/* Banner: Torneio Finalizado */}
                    {isFinished && (
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shrink-0">
                                    <i className="fas fa-trophy text-lg"></i>
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-white uppercase">Torneio Finalizado</h4>
                                    <p className="text-xs text-slate-400">Evento encerrado com sucesso.</p>
                                </div>
                            </div>
                            <span className="text-[10px] font-bold text-purple-300 bg-purple-500/10 border border-purple-500/20 px-2.5 py-1 rounded-full uppercase">
                                Concluído
                            </span>
                        </div>
                    )}

                    {/* Controle Simples de Status para Torneios Ativos */}
                    {!isFinished && !isCancelled && (
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4 shadow-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Status do Torneio</span>
                            </div>

                            {/* Seletor direto e prático entre os status ativos */}
                            <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-950 rounded-xl border border-slate-800">
                                <button
                                    type="button"
                                    disabled={updatingStatus || isScheduled}
                                    onClick={() => handleSelectStatus('scheduled')}
                                    className={`py-2 px-1 text-center rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                        isScheduled 
                                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' 
                                            : 'text-slate-400 hover:text-white'
                                    }`}
                                >
                                    Agendado
                                </button>
                                <button
                                    type="button"
                                    disabled={updatingStatus || isOpen}
                                    onClick={() => handleSelectStatus('open')}
                                    className={`py-2 px-1 text-center rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                        isOpen 
                                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' 
                                            : 'text-slate-400 hover:text-white'
                                    }`}
                                >
                                    Aberto
                                </button>
                                <button
                                    type="button"
                                    disabled={updatingStatus || (!isScheduled && !isOpen && !isFinished && !isCancelled)}
                                    onClick={() => handleSelectStatus('in_progress')}
                                    className={`py-2 px-1 text-center rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                        (!isScheduled && !isOpen && !isFinished && !isCancelled) 
                                            ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                                            : 'text-slate-400 hover:text-white'
                                    }`}
                                >
                                    Em Andamento
                                </button>
                            </div>

                            {/* Ações: Finalizar Torneio ou Cancelar */}
                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    type="button"
                                    disabled={submitting || updatingStatus}
                                    onClick={handleFinalize}
                                    className="flex-1 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold text-xs py-2.5 px-3 rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                                    title="Finalizar torneio e consolidar classificação"
                                >
                                    <i className="fas fa-trophy text-[11px]"></i>
                                    <span>Finalizar</span>
                                </button>
                                <button
                                    type="button"
                                    disabled={submitting || updatingStatus}
                                    onClick={() => setTargetStatusToConfirm('cancelled')}
                                    className="bg-slate-800 hover:bg-rose-950/40 hover:text-rose-300 hover:border-rose-800/40 border border-slate-700 text-slate-400 font-bold text-xs py-2.5 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                                    title="Cancelar torneio sem vencedores"
                                >
                                    <i className="fas fa-ban text-[11px]"></i>
                                    <span>Cancelar</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* CARD: INGRESSO & ESTOQUE DA LOJA */}
                    {(() => {
                        const ticket = Array.isArray(tournament.tickets) && tournament.tickets.length > 0 ? tournament.tickets[0] : null;
                        const ticketProduct = ticket?.product;

                        return (
                            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-black text-white italic tracking-tighter uppercase flex items-center space-x-2">
                                        <i className="fas fa-ticket-alt text-purple-400"></i>
                                        <span>INGRESSO</span>
                                    </h3>
                                    {ticketProduct ? (
                                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                            No Estoque
                                        </span>
                                    ) : (
                                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                            Sem Ingresso
                                        </span>
                                    )}
                                </div>

                                {ticketProduct ? (
                                    <div className="space-y-3">
                                        <div className="p-3 bg-slate-950/60 border border-slate-800 rounded-2xl flex items-center space-x-3">
                                            {ticketProduct.image_url ? (
                                                <img 
                                                    src={ticketProduct.image_url} 
                                                    alt={ticketProduct.name} 
                                                    className="w-12 h-12 rounded-xl object-cover border border-slate-800 shrink-0" 
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-xl bg-purple-900/30 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0">
                                                    <i className="fas fa-ticket-alt text-lg"></i>
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-bold text-white truncate">{ticketProduct.name}</p>
                                                <p className="text-[11px] font-black text-emerald-400">
                                                    R$ {Number(ticketProduct.msrp || ticketProduct.mspr || 0).toFixed(2)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2 text-center">
                                            <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-2.5">
                                                <span className="text-[9px] font-black uppercase text-slate-500 block">Vagas</span>
                                                <span className="text-sm font-black text-white">{ticket.max_quantity || 0}</span>
                                            </div>
                                            <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-2.5">
                                                <span className="text-[9px] font-black uppercase text-slate-500 block">Vendidos</span>
                                                <span className="text-sm font-black text-purple-400">{activeEntries.length}</span>
                                            </div>
                                            <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-2.5">
                                                <span className="text-[9px] font-black uppercase text-slate-500 block">Disponíveis</span>
                                                <span className="text-sm font-black text-emerald-400">
                                                    {Math.max(0, (ticket.max_quantity || tournament.max_players || 0) - activeEntries.length)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="pt-1">
                                            <button
                                                type="button"
                                                onClick={handleAddSlot}
                                                disabled={addingSlot || isCancelled || isFinished || !canManage}
                                                className="w-full bg-purple-950/40 hover:bg-purple-900/60 border border-purple-500/40 hover:border-purple-400 text-purple-200 hover:text-white text-xs font-black py-2.5 px-3 rounded-xl uppercase tracking-wider flex items-center justify-center space-x-2 transition-all shadow-sm active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                                                title={!canManage ? "Não é possível adicionar vagas após o início do torneio" : "Aumentar em 1 a coluna max_players da tabela tournaments e a quantity em store_products"}
                                            >
                                                <i className={`fas ${addingSlot ? 'fa-spinner fa-spin' : 'fa-plus-circle'} text-purple-400 text-xs`}></i>
                                                <span>Adicionar Vaga</span>
                                            </button>
                                        </div>

                                        {!canManage && (
                                            <div className="text-[10px] text-slate-400 bg-slate-950/80 border border-slate-800 rounded-xl p-2.5 flex items-center space-x-2">
                                                <i className="fas fa-lock text-slate-500 text-xs"></i>
                                                <span>Vagas e ingressos congelados após o início do torneio.</span>
                                            </div>
                                        )}
                                        <div className="pt-2 flex items-center justify-between">
                                            {canManage && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setTicketPrice(String(ticketProduct.msrp || ticketProduct.mspr || 10));
                                                        setTicketQuantity(String(ticket.max_quantity || tournament.max_players || 8));
                                                        setTicketMaxPerUser(String(ticket.max_per_user || 1));
                                                        setTicketIsActive(ticket.is_active !== false);
                                                        setShowTicketForm(!showTicketForm);
                                                    }}
                                                    className="text-[10px] uppercase font-black text-purple-400 hover:text-purple-300 transition-colors"
                                                >
                                                    {showTicketForm ? 'Fechar' : 'Ajustar Ingresso'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <p className="text-xs text-slate-400 leading-relaxed">
                                            Este torneio ainda não possui ingressos cadastrados no estoque da sua loja. Crie o produto agora para disponibilizar vagas à venda na loja.
                                        </p>
                                        {!showTicketForm && (
                                            <div className="space-y-2">
                                                {canManage && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setTicketQuantity(String(tournament.max_players || 8));
                                                            setShowTicketForm(true);
                                                        }}
                                                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-black py-3 rounded-2xl uppercase tracking-wider shadow-lg shadow-purple-900/30 flex items-center justify-center space-x-2 transition-all"
                                                    >
                                                        <i className="fas fa-plus-circle"></i>
                                                        <span>Criar Ingresso & Adicionar ao Estoque</span>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={handleAddSlot}
                                                    disabled={addingSlot || isCancelled || isFinished || !canManage}
                                                    className="w-full bg-purple-950/40 hover:bg-purple-900/60 border border-purple-500/40 hover:border-purple-400 text-purple-200 hover:text-white text-xs font-black py-2.5 rounded-xl uppercase tracking-wider flex items-center justify-center space-x-2 transition-all shadow-sm active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                                                    title={!canManage ? "Não é possível adicionar vagas após o início do torneio" : "Aumentar max_players em +1 e sincronizar com o estoque"}
                                                >
                                                    <i className={`fas ${addingSlot ? 'fa-spinner fa-spin' : 'fa-plus-circle'} text-purple-400 text-xs`}></i>
                                                    <span>Adicionar Vaga ao Torneio (+1 Vaga)</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {showTicketForm && (
                                    <form onSubmit={handleCreateTicketSubmit} className="pt-3 border-t border-slate-800 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1">
                                                    Preço (R$)
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    required
                                                    value={ticketPrice}
                                                    onChange={(e) => setTicketPrice(e.target.value)}
                                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white font-bold focus:outline-none focus:border-purple-500"
                                                    placeholder="10.00"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 pt-1">
                                            <div>
                                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1">
                                                    Limite por Usuário (max_per_user)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="20"
                                                    required
                                                    value={ticketMaxPerUser}
                                                    onChange={(e) => setTicketMaxPerUser(e.target.value)}
                                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white font-bold focus:outline-none focus:border-blue-500"
                                                    placeholder="1"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1">
                                                    Status da Venda (is_active)
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setTicketIsActive(!ticketIsActive)}
                                                    className={`w-full py-2 px-3 rounded-xl border text-xs font-black uppercase transition-all flex items-center justify-center space-x-1.5 ${
                                                        ticketIsActive 
                                                            ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' 
                                                            : 'bg-amber-500/10 border-amber-500/40 text-amber-400'
                                                    }`}
                                                >
                                                    <i className={`fas ${ticketIsActive ? 'fa-check-circle' : 'fa-pause-circle'}`}></i>
                                                    <span>{ticketIsActive ? 'Ativo' : 'Pausado'}</span>
                                                </button>
                                            </div>
                                        </div>

                                        <p className="text-[10px] text-slate-500 leading-tight">
                                            Vagas limitadas pela capacidade máxima do torneio ({tournament.max_players || 8}).
                                        </p>

                                        <div className="flex items-center space-x-2 pt-1">
                                            <button
                                                type="submit"
                                                disabled={creatingTicket}
                                                className="flex-1 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-xs font-black py-2.5 rounded-xl uppercase tracking-wider transition-all flex items-center justify-center space-x-2"
                                            >
                                                {creatingTicket ? (
                                                    <>
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                        <span>Salvando...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-check"></i>
                                                        <span>{ticketProduct ? 'Atualizar Ingresso' : 'Salvar e Adicionar'}</span>
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShowTicketForm(false)}
                                                className="px-3 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        );
                    })()}

                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl">
                        <h3 className="font-black text-white italic tracking-tighter uppercase mb-4 text-slate-500">INFO ADICIONAL</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-600 block mb-1">Link de Inscrição</label>
                                <div className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-[10px] text-purple-400 font-mono break-all group relative cursor-pointer" onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/torneio/${tournament.id}`);
                                    showNotification("Link copiado!", "success");
                                }}>
                                    <span className="opacity-60 group-hover:opacity-100 transition-opacity">Clique para copiar o link público</span>
                                </div>
                            </div>
                            <div>
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-600 block mb-1">Ticket de busca</label>
                                <div className="text-xl font-black text-white font-mono italic tracking-widest">{tournament.ticket}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal: Confirmação de Finalização ou Cancelamento (Ação Definitiva) */}
            {targetStatusToConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                    <div className={`bg-slate-900 border w-full max-w-md rounded-2xl p-6 sm:p-7 space-y-5 relative shadow-2xl ${
                        targetStatusToConfirm === 'cancelled' ? 'border-rose-800/80' : 'border-purple-600/80'
                    }`}>
                        <div className="flex items-center space-x-3">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg shrink-0 border ${
                                targetStatusToConfirm === 'cancelled' 
                                    ? 'bg-rose-500/20 border-rose-500/40 text-rose-400' 
                                    : 'bg-purple-500/20 border-purple-500/40 text-purple-400'
                            }`}>
                                <i className={targetStatusToConfirm === 'cancelled' ? 'fas fa-ban' : 'fas fa-trophy'}></i>
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-white italic tracking-tight uppercase">
                                    {targetStatusToConfirm === 'cancelled' ? 'Cancelar Torneio' : 'Finalizar Torneio'}
                                </h3>
                                <p className="text-xs text-slate-400 font-medium">Ação Definitiva</p>
                            </div>
                        </div>

                        <div className="space-y-3 text-xs text-slate-300 leading-relaxed">
                            <p>
                                Tem certeza de que deseja {targetStatusToConfirm === 'cancelled' ? 'cancelar' : 'finalizar'} o torneio <strong className="text-white">"{tournament.name}"</strong>?
                            </p>
                            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-[11px] text-amber-300 flex items-start gap-2.5">
                                <i className="fas fa-triangle-exclamation text-amber-400 mt-0.5 shrink-0"></i>
                                <span className="leading-snug">
                                    Ao {targetStatusToConfirm === 'cancelled' ? 'cancelar' : 'finalizar'}, esta ação é definitiva e você <strong>NÃO poderá mais alterar o status</strong> deste torneio posteriormente.
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2 pt-1">
                            <button
                                type="button"
                                disabled={updatingStatus}
                                onClick={() => setTargetStatusToConfirm(null)}
                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs py-2.5 rounded-xl transition-all uppercase tracking-wider cursor-pointer"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                disabled={updatingStatus}
                                onClick={() => executeStatusChange(targetStatusToConfirm)}
                                className={`flex-1 disabled:opacity-50 text-white font-black text-xs py-2.5 rounded-xl transition-all shadow-lg uppercase tracking-wider flex items-center justify-center space-x-2 cursor-pointer ${
                                    targetStatusToConfirm === 'cancelled'
                                        ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/20'
                                        : 'bg-purple-600 hover:bg-purple-500 shadow-purple-600/20'
                                }`}
                            >
                                {updatingStatus ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                        <span>Processando...</span>
                                    </>
                                ) : (
                                    <span>{targetStatusToConfirm === 'cancelled' ? 'Sim, Cancelar' : 'Sim, Finalizar'}</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Aviso de Ingresso Pago ao Rejeitar Participante */}
            {paidTicketWarningEntry && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-slate-900 border border-amber-500/40 w-full max-w-md rounded-2xl p-6 sm:p-7 space-y-5 relative shadow-2xl">
                        <div className="flex items-center space-x-3">
                            <div className="w-11 h-11 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 text-lg shrink-0">
                                <i className="fas fa-ticket text-amber-400"></i>
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-white italic tracking-tight uppercase">
                                    Participante com Ingresso Pago
                                </h3>
                                <p className="text-xs text-amber-400 font-bold">Confirmação de Rejeição</p>
                            </div>
                        </div>

                        <div className="space-y-3 text-xs text-slate-300 leading-relaxed">
                            <p>
                                O participante <strong className="text-white">{paidTicketWarningEntry.user?.fullname || paidTicketWarningEntry.user?.username || 'Selecionado'}</strong> {paidTicketWarningEntry.user?.username && <span className="text-slate-400">(@{paidTicketWarningEntry.user.username})</span>} <strong className="text-amber-300">comprou um ingresso pago</strong> para este torneio.
                            </p>
                            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3.5 text-[11px] text-slate-300 space-y-1.5">
                                <div className="flex items-center space-x-1.5 text-amber-400 font-bold">
                                    <i className="fas fa-triangle-exclamation"></i>
                                    <span>Ao recusar a inscrição:</span>
                                </div>
                                <ul className="list-disc list-inside space-y-1 text-slate-400 pl-1">
                                    <li>A vaga deste participante será <strong className="text-white">liberada para outro jogador</strong>.</li>
                                    <li>O status do participante mudará para <strong className="text-rose-400">Recusado</strong>.</li>
                                </ul>
                            </div>
                            <p className="text-slate-400 font-medium">
                                Deseja realmente prosseguir com a rejeição deste participante?
                            </p>
                        </div>

                        <div className="flex items-center space-x-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setPaidTicketWarningEntry(null)}
                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs py-2.5 rounded-xl transition-all uppercase tracking-wider cursor-pointer"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                onClick={confirmRejectPaidEntry}
                                className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-black text-xs py-2.5 rounded-xl transition-all shadow-lg shadow-rose-600/20 uppercase tracking-wider flex items-center justify-center space-x-1.5 cursor-pointer"
                            >
                                <i className="fas fa-ban text-[11px]"></i>
                                <span>Sim, Rejeitar</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Deck Preview for Organizer */}
            {previewDeck && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
                    <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl max-h-[85vh] rounded-2xl p-6 md:p-8 space-y-6 flex flex-col relative animate-in fade-in">
                        <button
                            onClick={() => setPreviewDeck(null)}
                            className="absolute top-6 right-6 text-slate-500 hover:text-white transition-colors"
                        >
                            <i className="fas fa-times text-lg"></i>
                        </button>

                        <div className="space-y-1">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-[10px] font-black text-purple-300 uppercase tracking-widest">
                                <i className="fas fa-layer-group"></i> Decklist Registrada
                            </div>
                            <h3 className="text-2xl font-black text-white">{previewDeck.name}</h3>
                            <p className="text-xs text-slate-400">
                                Decklist enviada pelo participante
                            </p>
                        </div>

                        {/* Cards List */}
                        <div className="flex-1 overflow-y-auto pr-2 space-y-2 divide-y divide-slate-800/60">
                            {(!previewDeck.data?.cards || previewDeck.data.cards.length === 0) ? (
                                <p className="text-xs text-slate-500 italic py-6 text-center">Nenhuma carta listada neste deck.</p>
                            ) : (
                                previewDeck.data.cards.map((card: any, idx: number) => (
                                    <div key={card.id || idx} className="pt-2 flex items-center justify-between text-xs">
                                        <div className="flex items-center space-x-3">
                                            <span className="font-mono font-bold text-purple-400 w-6 text-right">
                                                {card.quantity || 1}x
                                            </span>
                                            <span className="text-white font-medium">{card.name || `Carta #${card.id || idx}`}</span>
                                        </div>
                                        {card.type && (
                                            <span className="text-[10px] text-slate-500 font-mono bg-slate-950 px-2 py-0.5 rounded border border-slate-800">
                                                {card.type}
                                            </span>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="pt-4 border-t border-slate-800 flex justify-end">
                            <button
                                onClick={() => setPreviewDeck(null)}
                                className="bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs px-6 py-2.5 rounded-xl transition-all uppercase tracking-wider"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Export Participants */}
            {showExportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-3xl p-6 md:p-8 space-y-5 flex flex-col relative shadow-2xl">
                        <button
                            onClick={() => setShowExportModal(false)}
                            className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors"
                        >
                            <i className="fas fa-times text-lg"></i>
                        </button>

                        <div className="space-y-1">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-[10px] font-black text-purple-300 uppercase tracking-widest">
                                <i className="fas fa-file-arrow-down"></i> Exportar Participantes
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                                {tournament?.title}
                            </h3>
                            <p className="text-xs text-slate-400">
                                {entries.length} {entries.length === 1 ? 'participante inscrito' : 'participantes inscritos'} neste torneio.
                            </p>
                        </div>

                        {/* Format selector */}
                        <div className="flex bg-slate-950 p-1 rounded-2xl border border-slate-800 gap-1">
                            <button
                                onClick={() => setExportFormat('simple')}
                                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                    exportFormat === 'simple'
                                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/20'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                <i className="fas fa-list-ol text-xs"></i>
                                <span>Apenas Nomes</span>
                            </button>
                            <button
                                onClick={() => setExportFormat('detailed')}
                                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                    exportFormat === 'detailed'
                                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/20'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                <i className="fas fa-table-list text-xs"></i>
                                <span>Lista Detalhada</span>
                            </button>
                        </div>

                        {/* Preview Box */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                    Prévia da Lista
                                </span>
                                <span className="text-[10px] text-slate-500 font-mono">
                                    {entries.length} linha(s)
                                </span>
                            </div>
                            <textarea
                                readOnly
                                value={exportFormat === 'simple' ? getSimpleNamesList() : getDetailedNamesList()}
                                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                                rows={8}
                                className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-purple-500/50 resize-none select-all"
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2 border-t border-slate-800">
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="px-4 py-2.5 rounded-xl text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                Fechar
                            </button>

                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    onClick={handleDownloadCsv}
                                    className="px-3.5 py-2.5 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700/80 transition-all flex items-center gap-1.5"
                                    title="Baixar planilha para Excel / Google Planilhas"
                                >
                                    <i className="fas fa-file-csv text-emerald-400"></i>
                                    <span>Baixar CSV</span>
                                </button>
                                <button
                                    onClick={handleDownloadTxt}
                                    className="px-3.5 py-2.5 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700/80 transition-all flex items-center gap-1.5"
                                    title="Baixar arquivo de texto"
                                >
                                    <i className="fas fa-file-lines text-blue-400"></i>
                                    <span>Baixar TXT</span>
                                </button>
                                <button
                                    onClick={handleCopyList}
                                    className="px-4 py-2.5 rounded-xl text-xs font-bold bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-600/20 transition-all flex items-center gap-1.5"
                                >
                                    <i className={`fas ${copiedList ? 'fa-check' : 'fa-copy'}`}></i>
                                    <span>{copiedList ? 'Copiado!' : 'Copiar Lista'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Atribuição de Usuário ao Participante */}
            {showAssignModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-amber-500/30 rounded-2xl max-w-lg w-full p-6 shadow-2xl relative space-y-5 animate-in fade-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="flex items-start justify-between border-b border-slate-800 pb-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 text-lg shrink-0">
                                    <i className="fas fa-user-plus"></i>
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-white">
                                        Atribuir Usuário ao Participante
                                    </h3>
                                    <p className="text-xs text-slate-400">
                                        {selectedEntryToAssign?.ticket_unit 
                                            ? `Ingresso Unitário #${selectedEntryToAssign.ticket_unit}` 
                                            : `Inscrição #${selectedEntryToAssign?.id}`} • {tournament?.name || tournament?.title || 'Torneio'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowAssignModal(false);
                                    setSelectedEntryToAssign(null);
                                    setSelectedTargetUser(null);
                                }}
                                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
                            >
                                <i className="fas fa-times text-sm"></i>
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-slate-300 flex items-center justify-between">
                                <span>Pesquisar Jogador / Usuário</span>
                                <span className="text-[10px] text-slate-500 font-normal">Busque por @username ou nome</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={assignSearchQuery}
                                    onChange={(e) => handleSearchUsers(e.target.value)}
                                    placeholder="Digite o @username ou nome do jogador..."
                                    className="w-full bg-slate-950 border border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 rounded-xl pl-9 pr-8 py-2.5 text-xs text-white placeholder-slate-500 transition-colors outline-none"
                                    autoFocus
                                />
                                <div className="absolute left-3 top-3 text-slate-400 pointer-events-none text-xs">
                                    <i className="fas fa-search"></i>
                                </div>
                                {assignSearchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => handleSearchUsers('')}
                                        className="absolute right-3 top-2.5 text-slate-500 hover:text-slate-300 text-xs"
                                    >
                                        <i className="fas fa-times-circle"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Users Results List */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                <span>Jogadores Cadastrados</span>
                                {assignSearching && (
                                    <span className="text-amber-400 flex items-center space-x-1">
                                        <i className="fas fa-spinner fa-spin text-xs"></i>
                                        <span>Buscando...</span>
                                    </span>
                                )}
                            </div>

                            <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-800/40 border border-slate-800/80 rounded-xl bg-slate-950/40 p-1.5">
                                {assignSearchResults.length === 0 && !assignSearching ? (
                                    <div className="py-8 text-center text-slate-500 text-xs">
                                        {assignSearchQuery.trim() 
                                            ? 'Nenhum jogador encontrado com esse termo.' 
                                            : 'Nenhum usuário sugerido. Digite um nome ou @username.'}
                                    </div>
                                ) : (
                                    assignSearchResults.map((candidate) => {
                                        const isAlreadyIn = entries.some(
                                            e => Number(e.user_id) === Number(candidate.id) && Number(e.id) !== Number(selectedEntryToAssign?.id)
                                        );
                                        const isSelected = selectedTargetUser?.id === candidate.id;

                                        return (
                                            <div
                                                key={candidate.id}
                                                onClick={() => {
                                                    if (!isAlreadyIn) {
                                                        setSelectedTargetUser(candidate);
                                                    }
                                                }}
                                                className={`p-2.5 rounded-lg flex items-center justify-between transition-all select-none ${
                                                    isAlreadyIn
                                                        ? 'opacity-40 cursor-not-allowed bg-slate-900/30'
                                                        : isSelected
                                                        ? 'bg-amber-500/15 border border-amber-500/50 shadow-sm cursor-pointer'
                                                        : 'hover:bg-slate-800/60 cursor-pointer border border-transparent'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-8 h-8 rounded-full bg-slate-800 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                                                        {candidate.avatar ? (
                                                            <img src={candidate.avatar} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <span className="text-xs font-bold text-slate-400">
                                                                {candidate.username?.charAt(0) || candidate.fullname?.charAt(0) || '?'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-bold text-white flex items-center space-x-1.5">
                                                            <span>{candidate.fullname || candidate.username}</span>
                                                            {candidate.username && candidate.fullname && (
                                                                <span className="text-[10px] text-slate-400 font-mono font-normal">
                                                                    @{candidate.username}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-[10px] text-slate-500 font-mono">
                                                            ID #{candidate.id}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    {isAlreadyIn ? (
                                                        <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded-full">
                                                            Já no torneio
                                                        </span>
                                                    ) : isSelected ? (
                                                        <span className="text-amber-400 font-black text-xs flex items-center space-x-1">
                                                            <i className="fas fa-check-circle"></i>
                                                            <span>Selecionado</span>
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="text-[10px] font-bold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded-md transition-colors"
                                                        >
                                                            Escolher
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Selected User Preview Confirmation */}
                        {selectedTargetUser && (
                            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-between animate-in fade-in duration-150">
                                <div className="flex items-center space-x-3">
                                    <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/40 overflow-hidden shrink-0 flex items-center justify-center">
                                        {selectedTargetUser.avatar ? (
                                            <img src={selectedTargetUser.avatar} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-xs font-bold text-amber-300">
                                                {selectedTargetUser.username?.charAt(0) || '?'}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-white">
                                            {selectedTargetUser.fullname || selectedTargetUser.username}
                                        </div>
                                        <div className="text-[10px] text-amber-300/80 font-mono">
                                            @{selectedTargetUser.username} (ID: {selectedTargetUser.id})
                                        </div>
                                    </div>
                                </div>
                                <span className="text-[11px] font-bold text-amber-300 bg-amber-500/20 px-2.5 py-1 rounded-lg">
                                    Pronto para vincular
                                </span>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-3 border-t border-slate-800">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAssignModal(false);
                                    setSelectedEntryToAssign(null);
                                    setSelectedTargetUser(null);
                                }}
                                className="px-4 py-2 rounded-xl text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                Cancelar
                            </button>

                            <button
                                type="button"
                                onClick={handleConfirmAssign}
                                disabled={!selectedTargetUser || assignSubmitting}
                                className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center space-x-1.5 ${
                                    !selectedTargetUser || assignSubmitting
                                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700/50'
                                        : 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-black shadow-lg shadow-amber-500/20 cursor-pointer active:scale-95'
                                }`}
                            >
                                {assignSubmitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin text-xs"></i>
                                        <span>Vinculando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-link text-xs"></i>
                                        <span>Confirmar Atribuição</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
